import { useEffect, useMemo, useState } from "react";
import Icon from "@/components/icon";
import {
  Drawer,
  DrawerContent,
  DrawerTrigger,
  DrawerTitle,
  DrawerClose,
} from "@/components/ui/drawer";
import { useBackDismiss } from "@/hooks/use-back-dismiss";
import { cn } from "@/lib/cn";
import { ELEMENT_TYPES } from "@/features/analytics";
import type { FacetBucket, SearchFacets } from "@/models/search/SearchFacets";

/**
 * The list-shaped facets, in the order they are offered.
 *
 * `category` is deliberately absent: its own drawer renders the two-level tree
 * from the categories endpoint, which reads far better than the 50-odd flat
 * buckets the facet returns.
 *
 * Adding a group here is all it takes to offer it; a group the API sends as an
 * empty array renders nothing, so an unpopulated dimension costs nothing.
 */
export const FACET_GROUPS = [
  { facet: "level", param: "level", title: "سطح دوره" },
  { facet: "language", param: "lang", title: "زبان" },
  { facet: "educator", param: "edu", title: "مدرس" },
  { facet: "mini_app", param: "app", title: "آکادمی" },
  { facet: "target_audience", param: "aud", title: "مخاطب" },
] as const;

export type FacetGroup = (typeof FACET_GROUPS)[number];

/** Facet key → the `filter[…]` name the API expects for it. */
export const FACET_FILTER_KEYS = {
  level: "level",
  language: "language",
  educator: "educator_id",
  mini_app: "mini_app_uuid",
  target_audience: "target_audience_id",
} as const;

/** A group longer than this grows a search box on its own screen. */
const SEARCHABLE_SIZE = 12;

export interface FilterToggle {
  key: string;
  label: string;
  active: boolean;
  count?: number;
  onToggle: () => void;
}

interface Props {
  facets: SearchFacets | null;
  /** Selected values per group, keyed by the group's URL param. */
  selected: Record<string, string[]>;
  onToggle: (param: string, value: string) => void;
  onClearAll: () => void;
  /** Total active filters across every group and switch, for the badge. */
  activeCount: number;
  /** Boolean filters, shown as switches on the root screen. */
  toggles?: FilterToggle[];
}

type Bucket = FacetBucket<string | number | boolean>;

/** iOS-style switch. The whole row is the hit target, so this is presentational. */
function Switch({ on }: { on: boolean }) {
  return (
    <span
      className={cn(
        "relative h-5 w-9 shrink-0 rounded-full transition-colors",
        on ? "bg-primary" : "bg-default-200",
      )}
    >
      <span
        className={cn(
          "absolute top-0.5 size-4 rounded-full bg-white shadow transition-all",
          // The app is RTL, so "on" sits at the start edge of the track.
          on ? "start-0.5" : "start-[1.125rem]",
        )}
      />
    </span>
  );
}

/**
 * Square checkbox. The tick is inline rather than a sprite icon because the
 * sprite only carries circular check marks, which read as radio buttons — the
 * wrong affordance for a list where several options can be picked at once.
 * Presentational: the whole row is the hit target.
 */
function Checkbox({ checked }: { checked: boolean }) {
  return (
    <span
      className={cn(
        "flex size-[18px] shrink-0 items-center justify-center rounded-[5px] border-2 transition-colors",
        checked ? "border-primary bg-primary" : "border-default-300",
      )}
    >
      {checked && (
        <svg
          viewBox="0 0 24 24"
          className="size-3 text-white"
          fill="none"
          stroke="currentColor"
          strokeWidth={3.5}
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M4 12.5 9.5 18 20 6.5" />
        </svg>
      )}
    </span>
  );
}

/**
 * The root screen: one row per filter, not one row per *option*. A long option
 * list belongs behind its own screen — stacking every option of every group in
 * a single scroll buries the groups that matter.
 */
function RootScreen({
  groups,
  selected,
  toggles,
  onOpenGroup,
}: {
  groups: { group: FacetGroup; buckets: Bucket[] }[];
  selected: Record<string, string[]>;
  toggles: FilterToggle[];
  onOpenGroup: (group: FacetGroup) => void;
}) {
  return (
    <div className="divide-y divide-default-100">
      {groups.map(({ group, buckets }) => {
        const chosen = selected[group.param] ?? [];
        // Name the picks rather than counting them — "فارسی" tells the user
        // what they filtered by; "۱ مورد" makes them open the screen to find out.
        const summary = chosen.length
          ? buckets
              .filter((bucket) => chosen.includes(String(bucket.value)))
              .map((bucket) => bucket.label)
              .join("، ")
          : "همه";

        return (
          <button
            key={group.param}
            type="button"
            onClick={() => onOpenGroup(group)}
            className="flex w-full items-center gap-3 py-3.5 text-right"
            data-track-type={ELEMENT_TYPES.filterChip}
            data-track-id={`open:${group.param}`}
          >
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-medium text-default-800">
                {group.title}
              </span>
              <span
                className={cn(
                  "mt-0.5 block truncate text-xs",
                  chosen.length ? "text-primary" : "text-default-400",
                )}
              >
                {summary}
              </span>
            </span>
            {chosen.length > 0 && (
              <span className="flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-primary/10 px-1.5 text-[10px] font-bold text-primary">
                {chosen.length.toLocaleString("fa-IR")}
              </span>
            )}
            <Icon
              name="chevron-left"
              size={16}
              className="shrink-0 text-default-300"
            />
          </button>
        );
      })}

      {toggles.map((toggle) => (
        <button
          key={toggle.key}
          type="button"
          onClick={toggle.onToggle}
          className="flex w-full items-center gap-3 py-3.5 text-right"
          data-track-type={ELEMENT_TYPES.filterChip}
          data-track-id={toggle.key}
        >
          <span className="min-w-0 flex-1 text-sm font-medium text-default-800">
            {toggle.label}
          </span>
          {typeof toggle.count === "number" && (
            <span className="shrink-0 text-[11px] text-default-400">
              {toggle.count.toLocaleString("fa-IR")}
            </span>
          )}
          <Switch on={toggle.active} />
        </button>
      ))}
    </div>
  );
}

/** The drill-down screen for one group: search, then a checkbox list. */
function GroupScreen({
  group,
  buckets,
  selected,
  onToggle,
  onBack,
}: {
  group: FacetGroup;
  buckets: Bucket[];
  selected: string[];
  onToggle: (param: string, value: string) => void;
  onBack: () => void;
}) {
  const [term, setTerm] = useState("");

  const filtered = useMemo(() => {
    if (!term.trim()) return buckets;
    const needle = term.trim().toLowerCase();
    return buckets.filter((bucket) =>
      bucket.label.toLowerCase().includes(needle),
    );
  }, [buckets, term]);

  return (
    <div className="flex h-full flex-col">
      <div className="mb-2 flex items-center gap-2">
        <button
          type="button"
          onClick={onBack}
          aria-label="بازگشت"
          className="-me-1 shrink-0 p-1 text-default-700"
        >
          <Icon name="arrow-right" size={20} />
        </button>
        <DrawerTitle className="text-sm">{group.title}</DrawerTitle>
        {selected.length > 0 && (
          <span className="ms-auto text-[11px] font-medium text-primary">
            {selected.length.toLocaleString("fa-IR")} انتخاب
          </span>
        )}
      </div>

      {buckets.length > SEARCHABLE_SIZE && (
        <div className="relative mb-2">
          <input
            type="text"
            value={term}
            onChange={(event) => setTerm(event.target.value)}
            placeholder={`جستجو در ${group.title}`}
            className="w-full rounded-lg border border-default-200 bg-background py-2 pe-3 ps-8 text-xs text-default-800 placeholder:text-default-400 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary/70"
          />
          <span className="pointer-events-none absolute inset-y-0 start-2 flex items-center text-default-400">
            <Icon name="search" size={14} />
          </span>
        </div>
      )}

      <div className="-mx-4 min-h-0 flex-1 overflow-y-auto px-4">
        {filtered.length === 0 ? (
          <p className="py-6 text-center text-xs text-default-400">
            موردی یافت نشد
          </p>
        ) : (
          filtered.map((bucket) => {
            const value = String(bucket.value);
            const isSelected = selected.includes(value);
            // Zero means this option adds nothing on top of the other filters,
            // so it stays listed — the list must not shift under the user — but
            // it can't be picked into a dead end. A selected one stays live so
            // it can always be cleared.
            const isDead = bucket.count === 0 && !isSelected;
            return (
              <button
                key={value}
                type="button"
                disabled={isDead}
                onClick={() => onToggle(group.param, value)}
                className={cn(
                  "flex w-full items-center gap-2.5 rounded-lg p-2.5 text-right",
                  isDead && "opacity-40",
                )}
                data-track-type={ELEMENT_TYPES.filterChip}
                data-track-id={`${group.param}:${value}`}
              >
                <Checkbox checked={isSelected} />
                <span
                  className={cn(
                    "min-w-0 flex-1 truncate text-sm",
                    isSelected ? "text-primary" : "text-default-800",
                  )}
                >
                  {bucket.label}
                </span>
                <span className="shrink-0 text-[11px] text-default-400">
                  {bucket.count.toLocaleString("fa-IR")}
                </span>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}

/**
 * Every list-shaped filter the API offers, rendered from the response rather
 * than a hardcoded list — a dimension appears the moment courses start carrying
 * it, and disappears again if they stop.
 *
 * Renders nothing when the API sent no facets, which is also how it stays
 * invisible on an API whose search backend would reject these filters.
 */
export default function FacetFilterDrawer({
  facets,
  selected,
  onToggle,
  onClearAll,
  activeCount,
  toggles = [],
}: Props) {
  const [open, setOpen] = useState(false);
  const [activeGroup, setActiveGroup] = useState<FacetGroup | null>(null);

  // Reopening should land on the root screen, not wherever it was left.
  useEffect(() => {
    if (!open) setActiveGroup(null);
  }, [open]);

  // Drilling into a group is a screen of its own, so back leaves it the way the
  // arrow in its header does — one press per screen, then one to close.
  useBackDismiss(open && activeGroup !== null, () => setActiveGroup(null));

  const groups = FACET_GROUPS.map((group) => ({
    group,
    buckets: (facets?.[group.facet] ?? []) as Bucket[],
  })).filter(({ buckets }) => buckets.length > 0);

  // Unmounting while open would throw away the open state and shut the drawer
  // under the user's finger. A response can lose its facets at any time (the
  // search cluster hiccups), so an open drawer stays mounted regardless and
  // simply shows whatever it last had.
  if (groups.length === 0 && !open) return null;

  const activeBuckets =
    groups.find(({ group }) => group.param === activeGroup?.param)?.buckets ??
    [];

  return (
    <Drawer open={open} onOpenChange={setOpen}>
      <DrawerTrigger asChild>
        <button
          type="button"
          data-track-type={ELEMENT_TYPES.filterChip}
          data-track-id="more-filters"
          className={cn(
            "flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-medium border whitespace-nowrap transition flex-shrink-0",
            activeCount > 0
              ? "text-primary border-primary bg-primary/10"
              : "text-default-600 border-default-300 bg-background",
          )}
        >
          <Icon name="filter" size={16} />
          فیلترها
          {activeCount > 0 && (
            <span className="flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold text-white">
              {activeCount.toLocaleString("fa-IR")}
            </span>
          )}
        </button>
      </DrawerTrigger>

      <DrawerContent className="flex h-[78vh] flex-col">
        {/* Both screens live in one track that slides. The app is RTL, so
            drilling in moves the track rightwards: the group screen sits to the
            start side and comes into view as the root leaves. */}
        <div className="relative min-h-0 flex-1 overflow-hidden">
          <div
            className={cn(
              "absolute inset-0 flex flex-col transition-transform duration-300 ease-out",
              activeGroup
                ? "pointer-events-none translate-x-full opacity-0"
                : "translate-x-0 opacity-100",
            )}
          >
            <div className="mb-1 flex items-center justify-between">
              <DrawerTitle>فیلترها</DrawerTitle>
              {activeCount > 0 && (
                <button
                  type="button"
                  onClick={onClearAll}
                  className="text-xs font-medium text-danger"
                >
                  حذف همه
                </button>
              )}
            </div>

            <div
              className="-mx-4 min-h-0 flex-1 overflow-y-auto px-4"
              data-track-section="facet_filters"
            >
              <RootScreen
                groups={groups}
                selected={selected}
                toggles={toggles}
                onOpenGroup={setActiveGroup}
              />
            </div>
          </div>

          <div
            className={cn(
              "absolute inset-0 transition-transform duration-300 ease-out",
              activeGroup
                ? "translate-x-0 opacity-100"
                : "pointer-events-none -translate-x-full opacity-0",
            )}
          >
            {activeGroup && (
              <GroupScreen
                group={activeGroup}
                buckets={activeBuckets}
                selected={selected[activeGroup.param] ?? []}
                onToggle={onToggle}
                onBack={() => setActiveGroup(null)}
              />
            )}
          </div>
        </div>

        {activeGroup ? (
          <button
            type="button"
            onClick={() => setActiveGroup(null)}
            className="mt-3 w-full shrink-0 rounded-xl bg-primary py-3 text-sm font-bold text-white"
          >
            تایید
          </button>
        ) : (
          <DrawerClose asChild>
            <button
              type="button"
              className="mt-3 w-full shrink-0 rounded-xl bg-primary py-3 text-sm font-bold text-white"
            >
              مشاهده نتایج
            </button>
          </DrawerClose>
        )}
      </DrawerContent>
    </Drawer>
  );
}
